import { useCallback, useState } from 'react';
import { isNotEmpty, useForm } from '@mantine/form';
import {
  Title, Button, Text, Box, TextInput, LoadingOverlay,
  Stack, Group,
  type BoxProps,
  useMantineTheme
} from '@mantine/core';
import { useParams } from 'react-router-dom';

import { CheckCircle } from '../../../icons';
import type { StorageProviderConfig } from '../../../types/apiTypes';
import { SHAREPOINT_INTEGRATION_TYPE } from '../../../utils/apiUtils';
import useEnsisMutation from '../../../hooks/useEnsisMutation';

export interface CredentialsFormValues {
  clientId: string
  tenantId: string
  clientSecret: string
}

type Props = {
  storageProviderConfig?: StorageProviderConfig
  canEdit: boolean
} & BoxProps;

const CredentialsForm: React.FC<Props> = ({
  canEdit,
  storageProviderConfig,
  ...boxProps
}: Props) => {
  const { organization } = useParams();

  const storageProviderExists = storageProviderConfig !== undefined;
  const [editing, setEditing] = useState(!storageProviderExists);
  const { colors } = useMantineTheme();
  const form = useForm({
    initialValues: {
      clientId: storageProviderConfig?.credentials?.client_id ?? '',
      tenantId: storageProviderConfig?.credentials?.tenant_id ?? '',
      clientSecret: storageProviderConfig?.credentials?.client_secret ?? ''
    },
    validate: {
      clientId: isNotEmpty(),
      tenantId: isNotEmpty(),
      clientSecret: isNotEmpty()
    }
  });

  const updateStorageProviderSuccess = useCallback(
    (data: StorageProviderConfig) => {
      setEditing(false);
      const { credentials } = data;
      const initialValues = {
        clientId: credentials.client_id ?? '',
        tenantId: credentials.tenant_id ?? '',
        clientSecret: credentials.client_secret ?? ''
      };
      form.setInitialValues(initialValues);
      form.reset();
    },
    [form]
  );

  const handleResetForm = useCallback(() => {
    form.reset();
    setEditing(false);
  }, [form]);

  const editStorageProviderConfigMutation = useEnsisMutation(
    `/app/storage-providers/${storageProviderConfig?.uid}/data`,
    {
      contentType: 'application/json',
      requestType: 'patch',
      successMessage: 'SharePoint configuration updated',
      failureMessage: 'Invalid credentials provided',
      queryKeysToInvalidate: ['/app/storage-providers'],
      awaitRefetch: true,
      onSuccess: updateStorageProviderSuccess
    }
  );

  const createStorageProviderConfigMutation = useEnsisMutation(
    '/app/storage-providers/',
    {
      contentType: 'application/json',
      requestType: 'post',
      successMessage: 'SharePoint connected',
      failureMessage: 'Invalid credentials provided',
      queryKeysToInvalidate: ['/app/storage-providers'],
      awaitRefetch: true,
      onSuccess: updateStorageProviderSuccess
    }
  );

  const handleSubmit = useCallback(({
    tenantId,
    clientId,
    clientSecret
  }: CredentialsFormValues) => {
    const mutationVars = {
      tenant_id: tenantId,
      client_id: clientId,
      client_secret: clientSecret,
      organization_slug: organization ?? '',
      type: SHAREPOINT_INTEGRATION_TYPE
    };
    if (storageProviderExists) {
      editStorageProviderConfigMutation.mutate(mutationVars);
    } else {
      createStorageProviderConfigMutation.mutate(mutationVars);
    }
  }, [
    editStorageProviderConfigMutation.mutate,
    createStorageProviderConfigMutation.mutate,
    storageProviderExists
  ]);

  const submitDisabled = !form.isDirty() || !form.isValid();
  const formDisabled = !canEdit || !editing;
  const loading = createStorageProviderConfigMutation.isPending ||
    editStorageProviderConfigMutation.isPending;

  const footer = editing
    ? (
      <Group>
        <Button type="submit" w={138} loading={loading} disabled={submitDisabled}>
          {storageProviderExists ? 'Update' : 'Connect'}
        </Button>
        {storageProviderExists && (
          <Button variant="subtle" w={100} onClick={handleResetForm}>
            Cancel
          </Button>
        )}
      </Group>
      )
    : (
      <Stack align='flex-start'>
        <Group gap={8}>
          <CheckCircle color={colors.green[4]} />
          <Text c={colors.green[4]}>Sharepoint connected</Text>
        </Group>
        <Button
          disabled={storageProviderConfig?.is_syncing}
          p={0}
          variant='transparent'
          onClick={() => { setEditing(true); }}
        >
          Edit
        </Button>
      </Stack>
      );

  return (
    <Box {...boxProps}>
      <LoadingOverlay visible={loading} />
      <Box mb={16}>
        <Title order={6}>
          Credentials
        </Title>
        {!canEdit && <Text>  Contact your company admin to request changes to this information </Text>}
      </Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label='Client ID'
          disabled={formDisabled}
          w={548}
          mb={16}
          {...form.getInputProps('clientId')}
        />
        <TextInput
          label='Tenant ID'
          w={548}
          mb={16}
          disabled={formDisabled}
          {...form.getInputProps('tenantId')}
        />
        <TextInput
          label='Client Secret'
          w={548}
          mb={16}
          hidden={formDisabled}
          type={formDisabled ? 'password' : 'text'}
          disabled={formDisabled}
          {...form.getInputProps('clientSecret')}
        />
        {footer}
      </form>
    </Box>
  );
};

export default CredentialsForm;
